import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import useRolesAndPermissions from 'core/hooks/useRolesAndPermissions'
import {
  DashboardUrl,
  ScheduleDetailUrl,
  SchedulesUrl,
  TeamAutomationsUrl,
  TeamCategoriesUrl,
  TeamIntegrationsUrl,
  TeamMergeConfigImportMembersUrl,
  TeamMergeConfigTeamHierarchyUrl,
  TeamMergeConfigUrl,
  TeamMergeConfigUserFieldsUrl,
  TeamMergeUrl,
  TeamPlacesUrl,
  TeamPreferencesUrl,
  TeamProfileUrl,
  TeamProgramDetailUrl,
  TeamProgramPurposesUrl,
  TeamProgramsUrl,
  TeamReportDetailUrl,
  TeamRolesUrl,
  TeamSettingsPaymentsUrl,
  TeamSingleSignOnUrl,
  TeamTagsUrl
} from 'constants/RouteUrls'
import { useSettings } from 'core/hooks/useSettings'
import LazyLoadingSkeleton from 'core/components/loaders/LazyLoadingSkeleton'
import { lazyLoadHelper } from 'routes/resources'
import NotFoundPage from 'routes/NotFoundPage'

const AccountPreferencesPage = lazy(() =>
  lazyLoadHelper(import('pages/team/preferences/TeamPreferencesPage'))
)
const CategoriesPage = lazy(() =>
  lazyLoadHelper(import('pages/team/categories/CategoriesPage'))
)
const TeamPlacesPage = lazy(() =>
  lazyLoadHelper(import('pages/team/places/TeamPlacesPage'))
)
const SchedulesPage = lazy(() =>
  lazyLoadHelper(import('pages/team/schedules/SchedulesPage'))
)
const SingleSignOnPage = lazy(() =>
  lazyLoadHelper(
    import('pages/team/integrations/integrations/sso/SingleSignOnPage')
  )
)
const IntegrationsPage = lazy(() =>
  lazyLoadHelper(import('pages/team/integrations/IntegrationsPage'))
)
const MergePage = lazy(() =>
  lazyLoadHelper(import('pages/team/integrations/integrations/merge/MergePage'))
)

const ProgramPurposesPage = lazy(() =>
  lazyLoadHelper(import('pages/team/program/purposes/ProgramPurposesPage'))
)
const SettingsPaymentsPage = lazy(() =>
  lazyLoadHelper(import('pages/team/payments/PaymentsPage'))
)
const RulesPage = lazy(() =>
  lazyLoadHelper(import('pages/team/rules/RulesPage'))
)
const ProgramPage = lazy(() =>
  lazyLoadHelper(import('pages/team/program/ProgramPage'))
)
const TagsPage = lazy(() => lazyLoadHelper(import('pages/team/tags/TagsPage')))
const ProgramsPage = lazy(() =>
  lazyLoadHelper(import('pages/team/programs/ProgramsPage'))
)
const RolesAndPermissionsPage = lazy(() =>
  lazyLoadHelper(import('pages/team/roles-permissions/RolesPermissionsPage'))
)
const TeamProfilePage = lazy(() =>
  lazyLoadHelper(import('pages/team/profile/TeamProfilePage'))
)
const ReportPage = lazy(() =>
  lazyLoadHelper(import('pages/shared/report/ReportPage'))
)
const ScheduleDetailPage = lazy(() =>
  lazyLoadHelper(import('pages/team/schedules/ScheduleDetailPage'))
)
const MergeConfigPage = lazy(() =>
  lazyLoadHelper(
    import('pages/team/integrations/integrations/merge/config/MergeConfigPage')
  )
)
const ConfigurationModeSubPage = lazy(() =>
  lazyLoadHelper(
    import(
      'pages/team/integrations/integrations/merge/config/sub-pages/ConfigurationModeSubPage/ConfigurationModeSubPage'
    )
  )
)
const ImportMembersSubPage = lazy(() =>
  lazyLoadHelper(
    import(
      'pages/team/integrations/integrations/merge/config/sub-pages/ImportMembersSubPage/ImportMembersSubPage'
    )
  )
)

const UserFieldsSubPage = lazy(() =>
  lazyLoadHelper(
    import(
      'pages/team/integrations/integrations/merge/config/sub-pages/UserFieldsSubPage/UserFieldsSubPage'
    )
  )
)

const TeamHierarchySubPage = lazy(() =>
  lazyLoadHelper(
    import(
      'pages/team/integrations/integrations/merge/config/sub-pages/TeamHierarchySubPage/TeamHierarchySubPage'
    )
  )
)

const cleanWildcard = (string: string) => {
  return string.replace('/team/', '')
}

const TeamSpecificRoutes = () => {
  const {
    data: {
      featureFlags: { ENABLE_EVERLANCE_PAYMENTS }
    }
  } = useSettings()

  const {
    permissions: { hasAccessToTeamDashboard, hasFullPower }
  } = useRolesAndPermissions()

  if (!hasAccessToTeamDashboard) {
    return <Navigate to={DashboardUrl} replace />
  }

  return (
    <Routes>
      <Route
        path={cleanWildcard(TeamPreferencesUrl)}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="settings" />}>
            <AccountPreferencesPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamCategoriesUrl)}
        element={
          <Suspense
            fallback={
              <LazyLoadingSkeleton
                variant="default"
                withBreadcrumbs
                withBanner
              />
            }
          >
            <CategoriesPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamPlacesUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="default" withBreadcrumbs />}
          >
            <TeamPlacesPage />
          </Suspense>
        }
      />

      <Route
        path={cleanWildcard(TeamProfileUrl)}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="settings" />}>
            <TeamProfilePage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamRolesUrl)}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
            <RolesAndPermissionsPage />
          </Suspense>
        }
      />

      {/*/!*  Programs and rates routes*!/*/}
      <Route
        path={cleanWildcard(TeamProgramsUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="default" withBreadcrumbs />}
          >
            <ProgramsPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamTagsUrl)}
        element={
          <Suspense
            fallback={
              <LazyLoadingSkeleton
                variant="default"
                withBreadcrumbs
                withBanner
              />
            }
          >
            <TagsPage />
          </Suspense>
        }
      />

      <Route
        path={cleanWildcard(TeamProgramDetailUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="default" withBreadcrumbs />}
          >
            <ProgramPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(TeamProgramPurposesUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="default" withBreadcrumbs />}
          >
            <ProgramPurposesPage />
          </Suspense>
        }
      />

      {/* Automations */}
      <Route
        path={cleanWildcard(TeamAutomationsUrl)}
        element={
          <Suspense fallback={<LazyLoadingSkeleton variant="settings" />}>
            <RulesPage />
          </Suspense>
        }
      />

      {/*  Schedules routes*/}
      <Route
        path={cleanWildcard(SchedulesUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="default" withBreadcrumbs />}
          >
            <SchedulesPage />
          </Suspense>
        }
      />

      {/* Single Sign-On */}
      <Route
        path={cleanWildcard(TeamSingleSignOnUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="default" withBreadcrumbs />}
          >
            <SingleSignOnPage />
          </Suspense>
        }
      />

      {ENABLE_EVERLANCE_PAYMENTS && hasFullPower && (
        <Route
          path={cleanWildcard(TeamSettingsPaymentsUrl)}
          element={
            <Suspense fallback={<LazyLoadingSkeleton variant="settings" />}>
              <SettingsPaymentsPage />
            </Suspense>
          }
        />
      )}

      {hasFullPower && (
        <>
          <Route
            path={cleanWildcard(TeamIntegrationsUrl)}
            element={
              <Suspense
                fallback={<LazyLoadingSkeleton variant="integrations" />}
              >
                <IntegrationsPage />
              </Suspense>
            }
          />
          <Route
            path={cleanWildcard(TeamMergeUrl)}
            element={
              <Suspense
                fallback={
                  <LazyLoadingSkeleton variant="default" withBreadcrumbs />
                }
              >
                <MergePage />
              </Suspense>
            }
          />
        </>
      )}

      {/*Excluded from side menu*/}
      <Route
        path={cleanWildcard(TeamReportDetailUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="report" isChildPage />}
          >
            <ReportPage />
          </Suspense>
        }
      />
      <Route
        path={cleanWildcard(ScheduleDetailUrl)}
        element={
          <Suspense
            fallback={<LazyLoadingSkeleton variant="schedule" isChildPage />}
          >
            <ScheduleDetailPage />
          </Suspense>
        }
      />
      {hasFullPower && (
        <Route
          element={
            <Suspense
              fallback={<LazyLoadingSkeleton variant="settings" isChildPage />}
            >
              <MergeConfigPage />
            </Suspense>
          }
        >
          <Route
            path={cleanWildcard(TeamMergeConfigUrl)}
            element={
              <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
                <ConfigurationModeSubPage />
              </Suspense>
            }
          />
          <Route
            path={cleanWildcard(TeamMergeConfigImportMembersUrl)}
            element={
              <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
                <ImportMembersSubPage />
              </Suspense>
            }
          />
          <Route
            path={cleanWildcard(TeamMergeConfigUserFieldsUrl)}
            element={
              <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
                <UserFieldsSubPage />
              </Suspense>
            }
          />
          <Route
            path={cleanWildcard(TeamMergeConfigTeamHierarchyUrl)}
            element={
              <Suspense fallback={<LazyLoadingSkeleton variant="default" />}>
                <TeamHierarchySubPage />
              </Suspense>
            }
          />
        </Route>
      )}

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default TeamSpecificRoutes
